// Question loading for Turkey Hunt. Questions come from the course's quiz beat
// (the same bank the platform grades through quiz_questions); the game only
// accepts single-correct questions with exactly three choices.

import type { QuizQuestion, ScenarioSpec } from "../../scenario/types";

export interface GameQuestion {
  id: string;
  /** Position of the question in the source quiz beat (what /t/<token>/quiz expects). */
  position: number;
  prompt: string;
  topic: string;
  explanation: string;
  /** Choices in display order for this play (index 0..2 = turkey 1..3). */
  choices: { id: string; label: string }[];
  correctId: string;
}

export interface QuestionBank {
  beatId: string;
  /** Every question in the source beat, valid or not, in source order. */
  source: QuizQuestion[];
  valid: GameQuestion[];
  rejected: { id: string; reason: string }[];
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function validateQuestion(q: QuizQuestion): string | null {
  if (!q || typeof q.id !== "string" || !q.id) return "missing id";
  if (typeof q.prompt !== "string" || !q.prompt.trim()) return "missing prompt";
  if (!Array.isArray(q.options) || q.options.length !== 3) return "needs exactly 3 choices";
  const ids = new Set(q.options.map((o) => o.id));
  if (ids.size !== 3 || q.options.some((o) => !o.id || !o.label?.trim())) return "choices need unique ids and text";
  const correct = q.options.filter((o) => o.correct);
  if (correct.length !== 1) return "needs exactly one correct choice";
  return null;
}

export function loadBank(scenario: ScenarioSpec, beatId?: string): QuestionBank {
  const beat = scenario.beats.find((b) => (beatId ? b.id === beatId : b.type === "quiz") && Array.isArray(b.quiz));
  const source = beat?.quiz ?? [];
  const valid: GameQuestion[] = [];
  const rejected: { id: string; reason: string }[] = [];
  source.forEach((q, position) => {
    const reason = validateQuestion(q);
    if (reason) {
      rejected.push({ id: q?.id ?? `#${position}`, reason });
      return;
    }
    valid.push({
      id: q.id,
      position,
      prompt: q.prompt,
      topic: q.topic || q.title || "General",
      explanation: q.rationale || "",
      choices: q.options.map((o) => ({ id: o.id, label: o.label })),
      correctId: q.options.find((o) => o.correct)!.id,
    });
  });
  if (rejected.length) console.warn("[turkeyhunt] skipped questions", rejected);
  return { beatId: beat?.id ?? "", source, valid, rejected };
}

/** A fresh play order: questions shuffled, answers shuffled per question. */
export function sessionQuestions(bank: QuestionBank, count: number): GameQuestion[] {
  return shuffle(bank.valid)
    .slice(0, Math.max(1, Math.min(count, bank.valid.length)))
    .map((q) => ({ ...q, choices: shuffle(q.choices) }));
}
